import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { briefHTML } from '../../lib/brief.js';
import { BRIEF_STYLES } from '../../lib/brief-styles.js';
import { createCommissionSSE } from '../../lib/commission-sse.js';
import './sow-view.js';
import { loadConfig } from '../../lib/config.js';
import {
    downloadMarkdown,
    formatCents,
    phaseDisplayName,
    sizeLabel,
    versionLabel,
} from '../../lib/format.js';
import { submissionProcessing } from '../../lib/phase-submissions.js';
import { router } from '../../lib/router.js';
import { commissionTemplateLinks } from '../../lib/templates.js';
import '../shared/image-lightbox.js';
import '../shared/media-thumb.js';
import './tile-brush-previews.js';
// Side-effect import: registers <miniature-grid-preview>, shared with the
// miniature detail view so a reviewer sees the art placed exactly as it is there.
import '../miniatures/miniature-grid-preview.js';

const STATUS_COLORS = {
    assigned: 'bg-info',
    completed: 'bg-success',
};

class StaffAssignmentsView extends BaseComponent {
    static properties = {
        _commissions: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true },
        _selected: { type: Object, state: true },
        _phases: { type: Array, state: true },
        _selectedSubmissionId: { type: String, state: true },
        _config: { type: Object, state: true },
        _miniatureSizes: { type: Object, state: true },
        _miniatureCellPx: { type: Number, state: true },
        _reviewNote: { type: String, state: true },
        _completeSource: { type: String, state: true },
        _completeAssetId: { type: String, state: true },
        _completeAssetSelected: { type: Object, state: true },
        _completeAssetQuery: { type: String, state: true },
        _completeAssetResults: { type: Array, state: true },
        _completeAssetSearching: { type: Boolean, state: true },
        _completeMode: { type: String, state: true },
        // Whether completing publishes the asset to the freelancer catalog
        // (issue #205). On by default: accepting the delivery means it's finished.
        _completePublish: { type: Boolean, state: true },
        _completing: { type: Boolean, state: true },
        _completedResult: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _lightboxSrc: { type: String, state: true },
        _modal: { type: String, state: true },
        _detailTab: { type: String, state: true },
        _availableSources: { type: Array, state: true },
        _confirmUnassign: { type: Boolean, state: true },
        _activitySince: { type: String, state: true }, // datetime-local string in user's timezone
        _activityRows: { type: Array, state: true },
        _activityLoading: { type: Boolean, state: true },
        _activityError: { type: String, state: true },
    };

    static styles = [
        BRIEF_STYLES,
        css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; }
        .detail-header-left { display: flex; align-items: center; gap: 1rem; }
        .detail-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .detail-section { border: 1px solid #eee; border-radius: 8px; padding: 1.25rem; margin-bottom: 1.25rem; }
        .detail-section h6 { font-weight: 600; margin-bottom: 1rem; }
        .brief-text { font-size: 0.9rem; }
        .spec-text { white-space: pre-wrap; font-size: 0.9rem; line-height: 1.6; }
        .detail-layout { display: flex; gap: 2rem; }
        .detail-main { flex: 1; min-width: 0; }
        .detail-sidebar { width: 280px; flex-shrink: 0; }
        .ref-images-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
        .ref-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .ref-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; }
        .ref-thumb img:hover { opacity: 0.8; }
        .ref-thumb-info { padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666; }
        .spec-link {
            display: inline-flex; align-items: center; gap: 0.5rem;
            padding: 0.5rem 0.75rem; border: 1px solid #dee2e6; border-radius: 0.375rem;
            text-decoration: none; color: #333; font-size: 0.9rem;
        }
        .spec-link:hover { background: #f8f9fa; border-color: #adb5bd; }
        .spec-link i { color: #666; }
        .modal-overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.5);
            display: flex; align-items: center; justify-content: center; z-index: 9998;
        }
        .modal-panel {
            background: white; border-radius: 8px; width: 90vw; max-width: 700px; max-height: 85vh;
            display: flex; flex-direction: column; box-shadow: 0 1rem 3rem rgba(0,0,0,0.2);
        }
        .modal-header {
            display: flex; align-items: center; justify-content: space-between;
            padding: 1rem 1.25rem; border-bottom: 1px solid #dee2e6;
        }
        .modal-header h5 { margin: 0; font-weight: 600; }
        .modal-body { padding: 1.25rem; overflow-y: auto; flex: 1; }
        .modal-footer {
            display: flex; justify-content: flex-end; gap: 0.5rem;
            padding: 0.75rem 1.25rem; border-top: 1px solid #dee2e6;
        }
        .status-badge { font-size: 0.75rem; }
        .phase-step {
            border: 1px solid #eee; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 0.75rem;
        }
        .phase-step.submitted { border-color: #0dcaf0; background: #f0faff; }
        .phase-step.accepted { border-color: #198754; background: #f0faf4; }
        .phase-step.changes-requested { border-color: #ffc107; background: #fffdf0; }
        .phase-step-header {
            display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem;
        }
        .phase-step-title { font-weight: 600; font-size: 0.95rem; }
        .submission-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 0.75rem; }
        .submission-thumb {
            width: 120px; border: 2px solid #dee2e6; border-radius: 6px; overflow: hidden; cursor: pointer;
        }
        .submission-thumb.approved { border-color: #198754; }
        .submission-thumb.selected { border-color: #0d6efd; box-shadow: 0 0 0 2px rgba(13,110,253,0.25); }
        .submission-thumb img { width: 100%; aspect-ratio: 1; object-fit: contain; background: #f8f9fa; transition: opacity 0.15s; cursor: pointer; }
        .submission-thumb img:hover { opacity: 0.8; }
        .submission-thumb-info { padding: 0.25rem 0.5rem; font-size: 0.7rem; color: #666; }
        .submission-processing { padding: 0 0.5rem 0.35rem; font-size: 0.7rem; color: #666; }
        .submission-processing.failed { color: #b02a37; }
    `,
    ];

    constructor() {
        super();
        this._commissions = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._selected = null;
        this._phases = [];
        this._selectedSubmissionId = null;
        this._miniatureSizes = {};
        this._miniatureCellPx = 0;
        this._reviewNote = '';
        this._completeSource = '';
        this._completeAssetId = '';
        this._completeAssetSelected = null;
        this._completeAssetQuery = '';
        this._completeAssetResults = [];
        this._completeAssetSearching = false;
        this._completeMode = 'new';
        this._completePublish = true;
        this._completing = false;
        this._completedResult = null;
        this._saving = false;
        this._lightboxSrc = '';
        this._modal = null;
        this._detailTab = 'submissions';
        this._availableSources = [];
        this._confirmUnassign = false;
        this._activitySince = '';
        this._activityRows = [];
        this._activityLoading = false;
        this._activityError = null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._unsubscribeRouter = router.subscribe(() => this._syncFromRoute());
        this._syncFromRoute();
        loadConfig()
            .then((cfg) => {
                this._config = cfg;
                this._miniatureSizes = cfg.miniatureSizes || {};
                this._miniatureCellPx = cfg.miniatureCellPx || 0;
            })
            .catch(() => {});
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsubscribeRouter) this._unsubscribeRouter();
        this._disconnectSSE();
    }

    async _syncFromRoute() {
        const path = router.getPath();
        if (path === '/staff-assignments/activity') {
            this._disconnectSSE();
            this._selected = null;
            this._view = 'activity';
            const since = router.getQuery().get('since');
            this._activitySince = since
                ? this._isoToLocalInput(since)
                : this._defaultActivitySince();
            await this._loadActivity();
        } else if (path.match(/^\/staff-assignments\/[^/]+$/) && router.params.id) {
            this._completedResult = null;
            this._completePublish = true;
            await this._loadDetail(router.params.id);
            this._view = 'detail';
        } else {
            this._disconnectSSE();
            this._view = 'list';
            this._selected = null;
            this._completedResult = null;
            this._loadAssignments();
        }
    }

    /**
     * Convert an RFC3339 / ISO timestamp into a `<input type="datetime-local">`
     * value in the user's local timezone.
     */
    _isoToLocalInput(iso) {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return this._defaultActivitySince();
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    async _loadAssignments() {
        this._loading = true;
        this._error = null;
        try {
            const data = await api.get('/api/commissions/assigned');
            this._commissions = data.commissions || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    async _loadDetail(id) {
        this._error = null;
        try {
            this._selected = await api.get(`/api/commissions/${id}`);
            await this._loadPhases(id);
            await this._loadAvailableSources();
            this._connectSSE(id);
        } catch (err) {
            this._error = err.message;
        }
    }

    async _loadPhases(id) {
        try {
            const data = await api.get(`/api/commissions/${id}/phases`);
            this._phases = data.phases || [];
        } catch {
            this._phases = [];
        }
    }

    async _loadAvailableSources() {
        try {
            const data = await api.get('/api/sources');
            this._availableSources = data.sources || [];
            const defaultSource = this._availableSources.find((s) => s.isDefault);
            if (defaultSource) this._completeSource = defaultSource.slug;
        } catch {
            this._availableSources = [];
        }
    }

    _connectSSE(commissionId) {
        this._disconnectSSE();
        this._sse = createCommissionSSE(commissionId, (event) => this._handleSSEEvent(event));
        this._sse.connect();
    }

    _disconnectSSE() {
        if (this._sse) {
            this._sse.disconnect();
            this._sse = null;
        }
    }

    _handleSSEEvent(_event) {
        if (this._selected) {
            this._loadPhases(this._selected.id);
        }
    }

    async _acceptPhase(phaseId, submissions) {
        const approvedId =
            this._selectedSubmissionId ||
            (submissions && submissions.length === 1 ? submissions[0].id : null);
        if (!approvedId) {
            this._error = 'Select a submission to approve';
            return;
        }
        this._saving = true;
        this._error = null;
        try {
            await api.post(`/api/commissions/${this._selected.id}/phases/${phaseId}/accept`, {
                approved_submission_id: approvedId,
                note: this._reviewNote,
            });
            this._selectedSubmissionId = null;
            this._reviewNote = '';
            await this._loadDetail(this._selected.id);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _requestChanges(phaseId) {
        if (!this._reviewNote) {
            this._error = 'A note is required when requesting changes';
            return;
        }
        this._saving = true;
        this._error = null;
        try {
            await api.post(
                `/api/commissions/${this._selected.id}/phases/${phaseId}/request-changes`,
                {
                    note: this._reviewNote,
                },
            );
            this._reviewNote = '';
            await this._loadDetail(this._selected.id);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    _allPhasesAccepted() {
        return this._phases.length > 0 && this._phases.every((p) => p.status === 'accepted');
    }

    /**
     * Default activity-since timestamp: most recent Thursday at 4 PM in
     * the user's local timezone (the standing weekly meeting). Returned
     * as a `<input type="datetime-local">`-compatible string.
     * @returns {string}
     */
    _defaultActivitySince() {
        const now = new Date();
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 16, 0, 0, 0);
        // Walk back to the previous Thursday (day=4). If today is Thursday
        // before 4 PM, still go to last Thursday; if after 4 PM, today.
        const day = d.getDay();
        let offset = (day - 4 + 7) % 7;
        if (offset === 0 && now < d) offset = 7;
        d.setDate(d.getDate() - offset);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    async _loadActivity() {
        if (!this._activitySince) return;
        this._activityLoading = true;
        this._activityError = null;
        try {
            // datetime-local has no timezone; treat as user-local and convert to UTC.
            const since = new Date(this._activitySince).toISOString();
            const data = await api.get(
                `/api/commissions/activity?since=${encodeURIComponent(since)}`,
            );
            this._activityRows = data.activity || [];
        } catch (err) {
            this._activityError = err.message;
            this._activityRows = [];
        } finally {
            this._activityLoading = false;
        }
    }

    _onActivitySinceChange(value) {
        this._activitySince = value;
        if (!value) return;
        // Reflect in the URL so the chosen window is shareable / survives
        // reload, but use replaceState so each tweak doesn't litter history.
        const sinceIso = new Date(value).toISOString();
        const next = `/staff-assignments/activity?since=${encodeURIComponent(sinceIso)}`;
        window.history.replaceState({}, '', next);
        this._loadActivity();
    }

    _isRenderableMime(mt) {
        return mt === 'image/png' || mt === 'image/webp' || mt === 'image/jpeg';
    }

    /**
     * Bucket activity rows by commission. Returns commissions ordered by
     * their most recent event timestamp (newest first), each with the
     * raw events plus a small summary of state changes within the window.
     */
    _groupActivityByCommission(rows) {
        const map = new Map();
        for (const r of rows) {
            const cid = r.commission_id;
            if (!map.has(cid)) {
                map.set(cid, {
                    commission_id: cid,
                    commission_slug: r.commission_slug,
                    events: [],
                    latest: r.created_at,
                });
            }
            const group = map.get(cid);
            group.events.push(r);
            if (new Date(r.created_at) > new Date(group.latest)) group.latest = r.created_at;
        }
        const groups = Array.from(map.values());
        // Events come from the API in DESC order; per-group preserve that.
        groups.sort((a, b) => new Date(b.latest).getTime() - new Date(a.latest).getTime());
        return groups;
    }

    _activityEventLabel(event) {
        switch (event.event_type) {
            case 'commission:assigned':
                return event.payload?.self_claimed
                    ? `${event.actor_username || 'Someone'} claimed`
                    : `Assigned to ${event.payload?.freelancer_username || event.actor_username || 'a freelancer'}`;
            case 'commission:offered':
                return event.payload?.self_claimed
                    ? `${event.actor_username || 'Someone'} claimed — awaiting their signature`
                    : `Offered to ${event.payload?.freelancer_username || 'a freelancer'} — awaiting their signature`;
            case 'commission:offer-withdrawn':
                return event.payload?.previous_offeree_username
                    ? `Offer to ${event.payload.previous_offeree_username} withdrawn`
                    : 'Offer withdrawn';
            case 'commission:unassigned':
                return event.payload?.previous_freelancer_username
                    ? `Unassigned from ${event.payload.previous_freelancer_username}`
                    : 'Unassigned';
            case 'commission:completed':
                return `Completed → ${event.payload?.asset_slug || 'asset'}${event.payload?.published_to_catalog ? ' (published)' : ''}`;
            case 'submission:uploaded':
                return `${event.actor_username || 'Someone'} uploaded ${event.payload?.file_name || 'a file'} to ${event.payload?.phase_type || 'phase'}`;
            case 'submission:deleted':
                return `${event.actor_username || 'Someone'} removed a submission from ${event.payload?.phase_type || 'phase'}`;
            case 'phase:submitted':
                return `${event.actor_username || 'Someone'} submitted ${event.payload?.phase_type || 'phase'} for review`;
            case 'phase:accepted':
                return `${event.actor_username || 'Someone'} accepted ${event.payload?.phase_type || 'phase'}`;
            case 'phase:changes-requested':
                return `${event.actor_username || 'Someone'} requested changes on ${event.payload?.phase_type || 'phase'}`;
            default:
                return event.event_type;
        }
    }

    _renderActivity() {
        const groups = this._groupActivityByCommission(this._activityRows);
        return html`
            <a class="back-link" href="/staff-assignments"
                @click="${(e) => {
                    e.preventDefault();
                    router.navigate('/staff-assignments');
                }}">
                <i class="bi bi-arrow-left"></i> Back to Assignments
            </a>
            <div class="page-header">
                <h4>Activity</h4>
                <div class="d-flex align-items-end gap-2">
                    <div>
                        <label class="form-label small mb-1">Since</label>
                        <input type="datetime-local" class="form-control form-control-sm"
                            .value="${this._activitySince}"
                            @change="${(e) => this._onActivitySinceChange(e.target.value)}">
                    </div>
                </div>
            </div>
            <p class="page-subtitle">Everything that's happened across assignments since the selected time, grouped by commission.</p>

            ${this._activityError ? html`<div class="alert alert-danger">${this._activityError}</div>` : nothing}

            ${
                this._activityLoading && this._activityRows.length === 0
                    ? html`<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`
                    : groups.length === 0
                      ? html`<p class="text-muted text-center py-4">No activity in this window.</p>`
                      : html`
                        <div class="d-flex flex-column gap-3">
                            ${groups.map((g) => this._renderActivityGroup(g))}
                        </div>
                    `
            }
        `;
    }

    _renderActivityGroup(group) {
        const thumbs = group.events.filter(
            (e) =>
                e.event_type === 'submission:uploaded' &&
                e.submission_file_url &&
                this._isRenderableMime(e.submission_mime_type),
        );
        // Show non-upload events plus a one-line summary for uploads (the
        // images speak for themselves in the strip above).
        const nonUploadEvents = group.events.filter((e) => e.event_type !== 'submission:uploaded');
        const uploadCount = group.events.length - nonUploadEvents.length;
        return html`
            <div class="detail-section" style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 1rem;">
                <div class="d-flex align-items-baseline justify-content-between mb-2 gap-2">
                    <h6 class="mb-0">
                        <a href="/staff-assignments/${group.commission_id}" class="text-decoration-none"
                            @click="${(e) => {
                                e.preventDefault();
                                router.navigate(`/staff-assignments/${group.commission_id}`);
                            }}">
                            ${group.commission_slug}
                        </a>
                    </h6>
                    <span class="text-muted small">Last: ${new Date(group.latest).toLocaleString()}</span>
                </div>
                ${
                    thumbs.length > 0
                        ? html`
                    <div class="d-flex flex-wrap gap-2 mb-2">
                        ${thumbs.map(
                            (t) => html`
                            <a href="${t.submission_file_url}" target="_blank" rel="noopener"
                                title="${t.payload?.phase_type || ''} · ${t.submission_file_name || ''}"
                                style="display:block; width:96px;">
                                <img src="${t.submission_file_url}" alt="${t.submission_file_name || 'submission'}"
                                    style="width:96px; height:96px; object-fit:contain; background:#f8f9fa; border:1px solid #e9ecef; border-radius:4px;">
                                <div class="text-muted small text-truncate text-center" style="max-width: 96px;">${t.payload?.phase_type || ''}</div>
                            </a>
                        `,
                        )}
                    </div>
                `
                        : nothing
                }
                <ul class="list-unstyled mb-0">
                    ${
                        uploadCount > 0 && thumbs.length === 0
                            ? html`
                        <li class="small text-muted">${uploadCount} submission${uploadCount === 1 ? '' : 's'} uploaded (no preview)</li>
                    `
                            : nothing
                    }
                    ${nonUploadEvents.map(
                        (e) => html`
                        <li class="small">
                            <span class="text-muted me-1">${new Date(e.created_at).toLocaleString()}</span>
                            <span>${this._activityEventLabel(e)}</span>
                        </li>
                    `,
                    )}
                </ul>
            </div>
        `;
    }

    /**
     * Debounced search for existing miniatures by slug/name. Each keystroke
     * resets a small delay; only the last query in flight wins.
     * @param {string} query
     */
    _searchExistingMiniatures(query) {
        this._completeAssetQuery = query;
        if (this._completeAssetSelected) {
            this._completeAssetSelected = null;
            this._completeAssetId = '';
        }
        clearTimeout(this._completeAssetDebounce);
        if (!query.trim()) {
            this._completeAssetResults = [];
            this._completeAssetSearching = false;
            return;
        }
        this._completeAssetSearching = true;
        const requested = query;
        this._completeAssetDebounce = setTimeout(async () => {
            try {
                const params = new URLSearchParams({ query: requested, pageSize: '10' });
                const data = await api.get(`/api/assets/miniatures?${params}`);
                if (this._completeAssetQuery !== requested) return; // stale
                this._completeAssetResults = data.miniatures || [];
            } catch (err) {
                if (this._completeAssetQuery !== requested) return;
                this._completeAssetResults = [];
                this._error = err.message;
            } finally {
                if (this._completeAssetQuery === requested) {
                    this._completeAssetSearching = false;
                }
            }
        }, 200);
    }

    _switchToExistingMode() {
        this._completeMode = 'existing';
        // Focus the search input on the next render tick so the user can
        // start typing immediately.
        this.updateComplete.then(() => {
            const input = /** @type {HTMLInputElement | null} */ (
                this.renderRoot?.querySelector('.complete-existing-search')
            );
            input?.focus();
        });
    }

    _selectExistingMiniature(miniature) {
        this._completeAssetSelected = miniature;
        this._completeAssetId = miniature.id;
        this._completeAssetQuery = miniature.slug;
        this._completeAssetResults = [];
    }

    _clearExistingMiniatureSelection() {
        this._completeAssetSelected = null;
        this._completeAssetId = '';
        this._completeAssetQuery = '';
        this._completeAssetResults = [];
    }

    _renderExistingMiniaturePicker() {
        const selected = this._completeAssetSelected;
        if (selected) {
            return html`
                <div class="d-flex align-items-center gap-2 mb-2 p-2 border rounded" style="max-width: 350px; background: #fff;">
                    ${
                        selected.image_url
                            ? html`<img src="${selected.image_url}" alt="${selected.slug}" style="width:32px; height:32px; object-fit:contain; background:#f8f9fa; border-radius:4px;">`
                            : html`<div style="width:32px; height:32px; background:#dee2e6; border-radius:4px;"></div>`
                    }
                    <div class="flex-grow-1" style="min-width: 0;">
                        <div class="text-truncate" style="font-weight: 500;">${selected.slug}</div>
                        <div class="text-muted small text-truncate">${selected.miniature_type || ''}</div>
                    </div>
                    <button type="button" class="btn btn-link btn-sm p-0 text-danger" title="Change selection"
                        @click="${this._clearExistingMiniatureSelection}">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
            `;
        }
        return html`
            <div class="position-relative mb-2" style="max-width: 350px;">
                <input type="text" class="form-control form-control-sm complete-existing-search" placeholder="Search miniatures by name…"
                    .value="${this._completeAssetQuery}"
                    @input="${(e) => this._searchExistingMiniatures(e.target.value)}">
                ${
                    this._completeAssetQuery &&
                    (this._completeAssetResults.length > 0 || this._completeAssetSearching)
                        ? html`
                    <div class="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                        style="z-index: 10; max-height: 280px; overflow-y: auto;">
                        ${
                            this._completeAssetSearching && this._completeAssetResults.length === 0
                                ? html`
                            <div class="px-2 py-1 text-muted small">Searching…</div>
                        `
                                : this._completeAssetResults.map(
                                      (t) => html`
                            <button type="button" class="d-flex align-items-center gap-2 w-100 px-2 py-1 border-0 bg-transparent text-start"
                                style="cursor: pointer;"
                                @click="${() => this._selectExistingMiniature(t)}"
                                @mouseenter="${(e) => (e.currentTarget.style.background = '#f1f3f5')}"
                                @mouseleave="${(e) => (e.currentTarget.style.background = 'transparent')}">
                                ${
                                    t.image_url
                                        ? html`<img src="${t.image_url}" alt="${t.slug}" style="width:28px; height:28px; object-fit:contain; background:#f8f9fa; border-radius:4px;">`
                                        : html`<div style="width:28px; height:28px; background:#dee2e6; border-radius:4px;"></div>`
                                }
                                <div class="flex-grow-1" style="min-width: 0;">
                                    <div class="text-truncate small" style="font-weight: 500;">${t.slug}</div>
                                    <div class="text-muted text-truncate" style="font-size: 0.7rem;">${t.miniature_type || ''}</div>
                                </div>
                            </button>
                        `,
                                  )
                        }
                    </div>
                `
                        : nothing
                }
                ${
                    this._completeAssetQuery &&
                    !this._completeAssetSearching &&
                    this._completeAssetResults.length === 0
                        ? html`
                    <div class="form-text">No miniatures match "${this._completeAssetQuery}".</div>
                `
                        : nothing
                }
            </div>
        `;
    }

    async _completeCommission() {
        this._completing = true;
        this._error = null;
        this._completedResult = null;
        try {
            const body = {
                target_source: this._completeSource,
                publish: this._completePublish,
            };
            if (this._completeMode === 'existing' && this._completeAssetId) {
                body.target_asset_id = this._completeAssetId;
            }
            const result = await api.post(`/api/commissions/${this._selected.id}/complete`, body);
            this._completedResult = result;
            await this._loadDetail(this._selected.id);
            this._loadAssignments();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._completing = false;
        }
    }

    async _unassignCommission() {
        this._error = null;
        try {
            await api.post(`/api/commissions/${this._selected.id}/unassign`);
            this._confirmUnassign = false;
            router.navigate('/staff-assignments');
        } catch (err) {
            this._error = err.message;
        }
    }

    _phaseStatusBadge(status) {
        const colors = {
            pending: 'bg-secondary',
            'in-progress': 'bg-primary',
            submitted: 'bg-info',
            accepted: 'bg-success',
            'changes-requested': 'bg-warning text-dark',
        };
        return html`<span class="badge status-badge ${colors[status] || 'bg-secondary'}">${status}</span>`;
    }

    _renderReferenceImages(images) {
        if (!images || images.length === 0) return nothing;
        return html`
            <div class="detail-sidebar">
                <h6 style="font-weight: 600; font-size: 0.85rem; margin-bottom: 0.5rem;">Reference Images</h6>
                <div class="ref-images-grid">
                    ${images.map(
                        (img) => html`
                        <div class="ref-thumb">
                            <media-thumb src="${img.file_url}" alt="${versionLabel(img.version)}"
                                 @media-click="${() => (this._lightboxSrc = img.file_url)}"></media-thumb>
                            <div class="ref-thumb-info">${versionLabel(img.version)}</div>
                        </div>
                    `,
                    )}
                </div>
            </div>
        `;
    }

    _renderList() {
        return html`
            <div class="page-header">
                <h4>Assignments</h4>
                <a class="btn btn-outline-primary btn-sm" href="/staff-assignments/activity"
                    @click="${(e) => {
                        e.preventDefault();
                        router.navigate('/staff-assignments/activity');
                    }}">
                    <i class="bi bi-clock-history"></i> Activity
                </a>
            </div>
            <p class="page-subtitle">Review active commissions, phases, and submissions.</p>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Slug</th>
                            <th>Freelancer</th>
                            <th>Phase</th>
                            <th>Rate</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._commissions.map(
                            (c) => html`
                            <tr style="cursor: pointer;" @click="${() => router.navigate(`/staff-assignments/${c.id}`)}">
                                <td><span style="color: #0d6efd; font-weight: 500;">${c.slug}</span></td>
                                <td>${c.assigned_to_username || '-'}</td>
                                <td>
                                    ${
                                        c.current_phase_status === 'all-accepted'
                                            ? html`<span class="badge bg-success status-badge">Ready to Complete</span>`
                                            : c.current_phase
                                              ? html`
                                        <span>${phaseDisplayName(c.current_phase)}</span>
                                        ${c.current_phase_status === 'submitted' ? html` <span class="badge bg-info status-badge">pending review</span>` : ''}
                                        ${c.current_phase_status === 'changes-requested' ? html` <span class="badge bg-warning text-dark status-badge">changes requested</span>` : ''}
                                    `
                                              : html`<span class="text-muted">-</span>`
                                    }
                                </td>
                                <td>${formatCents(c.rate_cents)}</td>
                            </tr>
                        `,
                        )}
                    </tbody>
                </table>

                ${this._commissions.length === 0 ? html`<p class="text-muted text-center py-3">No active assignments.</p>` : nothing}
            `
            }
        `;
    }

    _renderDetail() {
        const c = this._selected;
        if (!c) return nothing;

        return html`
            <a class="back-link" href="/staff-assignments">
                <i class="bi bi-arrow-left"></i> Back to Assignments
            </a>

            <div class="detail-header">
                <div class="detail-header-left">
                    <h4>${c.slug}</h4>
                    <span class="badge ${STATUS_COLORS[c.status] || 'bg-secondary'}">${c.status}</span>
                </div>
                ${
                    c.assigned_to_username
                        ? html`
                    <div class="d-flex align-items-center gap-2">
                        <span class="text-muted">Freelancer: <strong>${c.assigned_to_username}</strong></span>
                        ${
                            c.status === 'assigned'
                                ? html`
                            ${
                                this._confirmUnassign
                                    ? html`
                                <button class="btn btn-danger btn-sm" @click="${this._unassignCommission}">Confirm Unassign</button>
                                <button class="btn btn-outline-secondary btn-sm" @click="${() => (this._confirmUnassign = false)}">Cancel</button>
                            `
                                    : html`
                                <button class="btn btn-outline-danger btn-sm" @click="${() => (this._confirmUnassign = true)}">Unassign</button>
                            `
                            }
                        `
                                : nothing
                        }
                    </div>
                `
                        : nothing
                }
            </div>

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._detailTab === 'submissions' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._detailTab = 'submissions';
                       }}">Submissions</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._detailTab === 'details' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._detailTab = 'details';
                       }}">Details</a>
                </li>
            </ul>

            ${
                this._detailTab === 'submissions'
                    ? html`
                ${
                    this._phases.length > 0
                        ? html`
                    <div class="detail-layout">
                        <div class="detail-main">
                            ${
                                this._completedResult
                                    ? html`
                                <div class="alert alert-success d-flex align-items-center justify-content-between">
                                    <div>
                                        <i class="bi bi-check-circle-fill me-2"></i>
                                        Commission completed and added to
                                        <strong>${this._completedResult.asset_slug || 'miniature'}</strong>${this._completedResult.published_to_catalog ? ', and published it' : ''}.
                                    </div>
                                    <a href="/miniatures" class="btn btn-sm btn-success" @click="${(
                                        e,
                                    ) => {
                                        e.preventDefault();
                                        router.navigate('/miniatures');
                                    }}">
                                        View miniature <i class="bi bi-arrow-right"></i>
                                    </a>
                                </div>
                            `
                                    : nothing
                            }

                            ${this._phases.map((p) => this._renderStaffPhase(p))}

                            ${
                                this._allPhasesAccepted() && c.status === 'assigned'
                                    ? html`
                                <div class="detail-section" style="border-color: #198754; background: #f0faf4;">
                                    <h6>Complete Commission</h6>
                                    <div class="mb-2">
                                        <label class="form-label small">Add to</label>
                                        <div class="d-flex gap-3 mb-2">
                                            <label class="form-check-label">
                                                <input type="radio" name="complete-mode" value="new" class="form-check-input"
                                                    ?checked="${this._completeMode === 'new'}"
                                                    @change="${() => (this._completeMode = 'new')}">
                                                New miniature (${c.slug})
                                            </label>
                                            <label class="form-check-label">
                                                <input type="radio" name="complete-mode" value="existing" class="form-check-input"
                                                    ?checked="${this._completeMode === 'existing'}"
                                                    @change="${() => this._switchToExistingMode()}">
                                                Existing miniature
                                            </label>
                                        </div>
                                        ${
                                            this._completeMode === 'existing'
                                                ? html`
                                            ${this._renderExistingMiniaturePicker()}
                                        `
                                                : ''
                                        }
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label small">Source</label>
                                        <select class="form-select form-select-sm" style="max-width: 200px;"
                                            @change="${(e) => (this._completeSource = e.target.value)}">
                                            <option value="">Select source...</option>
                                            ${(this._availableSources || []).map(
                                                (s) => html`
                                                <option value="${s.slug}" ?selected="${this._completeSource === s.slug}">${s.label}</option>
                                            `,
                                            )}
                                        </select>
                                    </div>
                                    <div class="form-check mb-3">
                                        <input class="form-check-input" type="checkbox" id="complete-publish"
                                            .checked="${this._completePublish}"
                                            @change="${(e) => (this._completePublish = e.target.checked)}">
                                        <label class="form-check-label small" for="complete-publish">
                                            Publish
                                            <span class="text-muted d-block">Freelancers can browse and download the finished art in the Asset Catalog, and apps will use it to leave out art that isn’t published.</span>
                                        </label>
                                    </div>
                                    <button class="btn btn-success" ?disabled="${this._completing || !this._completeSource || (this._completeMode === 'existing' && !this._completeAssetId)}"
                                        @click="${this._completeCommission}">
                                        ${this._completing ? 'Completing...' : 'Complete Commission'}
                                    </button>
                                </div>
                            `
                                    : nothing
                            }
                        </div>

                        ${this._renderReferenceImages(c.reference_images)}
                    </div>
                `
                        : html`<p class="text-muted">No phases available.</p>`
                }
            `
                    : ''
            }

            ${
                this._detailTab === 'details'
                    ? html`
                <div class="detail-layout">
                    <div class="detail-main">
                        <div class="detail-section">
                            <div class="row mb-2">
                                <div class="col-6">
                                    <span class="text-muted small">Type</span>
                                    <div class="fw-medium">${c.asset_type}</div>
                                </div>
                                <div class="col-6">
                                    <span class="text-muted small">Rate</span>
                                    <div class="fw-medium">${formatCents(c.rate_cents)}</div>
                                </div>
                            </div>
                            ${
                                (c.asset_type === 'miniature' || c.asset_type === 'tactical-map') &&
                                c.miniature_size
                                    ? html`
                                <div class="row mb-2">
                                    <div class="col-6">
                                        <span class="text-muted small">Size</span>
                                        <div class="fw-medium">${sizeLabel(c.asset_type, c.miniature_size)}</div>
                                    </div>
                                    ${
                                        c.asset_type === 'miniature'
                                            ? html`
                                        <div class="col-6">
                                            <span class="text-muted small">Miniature Type</span>
                                            <div class="fw-medium">${c.miniature_type ? (c.miniature_type === 'pc' ? 'PC' : c.miniature_type === 'monster' ? 'Monster' : c.miniature_type) : html`<span class="text-muted">Not set</span>`}</div>
                                        </div>
                                    `
                                            : nothing
                                    }
                                </div>
                            `
                                    : nothing
                            }
                            ${
                                c.assigned_to_username
                                    ? html`
                                <div class="row">
                                    <div class="col-6">
                                        <span class="text-muted small">Assigned To</span>
                                        <div class="fw-medium">${c.assigned_to_username}</div>
                                    </div>
                                </div>
                            `
                                    : nothing
                            }
                        </div>

                        <div class="detail-section">
                            <h6>Brief</h6>
                            <div class="brief-text brief-md" .innerHTML="${briefHTML(c.brief)}"></div>
                        </div>

                        <div class="detail-section">
                            <h6>Statement of Work</h6>
                            ${
                                c.staff_assignment
                                    ? html`<p class="text-muted mb-0">Staff assignment, no SOW.</p>`
                                    : html`<sow-view
                                          commission-id="${c.id}"
                                          staff
                                          @sow-signed="${() => this._loadDetail(c.id)}"
                                      ></sow-view>`
                            }
                        </div>

                        <div class="detail-section">
                            <h6>Reference Documents</h6>
                            <div class="d-flex gap-3">
                                ${
                                    c.style_guide
                                        ? html`
                                    <a class="spec-link" href="#" @click="${(e) => {
                                        e.preventDefault();
                                        this._modal = 'style-guide';
                                    }}">
                                        <i class="bi bi-palette"></i> Style Guide
                                    </a>
                                `
                                        : nothing
                                }
                                ${
                                    c.technical_spec
                                        ? html`
                                    <a class="spec-link" href="#" @click="${(e) => {
                                        e.preventDefault();
                                        this._modal = 'technical-spec';
                                    }}">
                                        <i class="bi bi-rulers"></i> Technical Spec
                                    </a>
                                `
                                        : nothing
                                }
                                ${commissionTemplateLinks(c, this._config).map(
                                    (link) => html`
                                        <a class="spec-link" href="${link.url}" download>
                                            <i class="bi bi-file-earmark-arrow-down"></i> ${link.label}
                                        </a>
                                    `,
                                )}
                            </div>
                        </div>
                    </div>

                    ${this._renderReferenceImages(c.reference_images)}
                </div>
            `
                    : ''
            }

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>
        `;
    }

    // A miniature submission, or an image submitted as a PSD, is flattened
    // server-side into a "rendered" layer; show that image, not a PSD placeholder.
    _renderedLayerUrl(s) {
        const type = this._selected?.asset_type;
        if ((type === 'miniature' || type === 'image') && s?.layers?.length) {
            return s.layers[0].file_url;
        }
        return null;
    }

    // Where a PSD submission's processing stands, shown under its thumbnail so
    // the reviewer knows a PSD is still processing or why it failed.
    _renderSubmissionProcessing(s) {
        const { state, message } = submissionProcessing(s);
        if (state === 'processing') {
            return html`<div class="submission-processing d-flex align-items-center gap-1">
                <span class="spinner-border spinner-border-sm" role="status"></span>${message}
            </div>`;
        }
        if (state === 'failed') {
            return html`<div class="submission-processing failed">${message}</div>`;
        }
        return nothing;
    }

    // The submission whose art the grid preview shows: the one being reviewed if
    // the reviewer picked it, otherwise the approved one, otherwise the newest.
    _previewSubmission(phase) {
        const submissions = phase.submissions || [];
        return (
            submissions.find((s) => s.id === this._selectedSubmissionId) ||
            submissions.find((s) => s.id === phase.approved_submission_id) ||
            submissions[0]
        );
    }

    // Grid dimensions for the commission's miniature size, from the api's size
    // vocabulary (/api/config `miniatureSizes`).
    _miniatureGridSpec() {
        const spec = this._miniatureSizes?.[this._selected?.miniature_size];
        return {
            footprintCells: spec?.footprintCells ?? 0,
            canvasCells: spec?.canvasCells ?? 0,
            cellPx: this._miniatureCellPx,
        };
    }

    // The reviewer's grid preview: miniature art may overflow the cells the
    // miniature occupies, and approving that overhang blind is exactly what this
    // exists to prevent. Sits below the thumb strip rather than replacing the
    // thumbs — grid lines don't read at ~100px, and the thumbs are for scanning
    // versions. Mirrors where <tile-brush-previews> sits for brush commissions.
    _renderMiniatureGridPreview(phase) {
        if (this._selected?.asset_type !== 'miniature') {
            return nothing;
        }
        const submission = this._previewSubmission(phase);
        const src = this._renderedLayerUrl(submission);
        if (!src) {
            // A PSD's preview is flattened on the server's job queue after upload;
            // the view refetches when it lands (submission:extracted).
            return submission?.mime_type === 'image/vnd.adobe.photoshop'
                ? html`<div class="mt-3 text-muted small d-flex align-items-center gap-2">
                      <span class="spinner-border spinner-border-sm" role="status"></span>
                      The miniature preview appears here once the PSD has been processed.
                  </div>`
                : nothing;
        }
        const grid = this._miniatureGridSpec();
        return html`
            <div class="mt-3">
                <miniature-grid-preview
                    src="${src}"
                    footprint-cells="${grid.footprintCells}"
                    canvas-cells="${grid.canvasCells}"
                    cell-px="${grid.cellPx}"
                    cell-size="64"></miniature-grid-preview>
                <div class="text-muted small mt-1">
                    ${versionLabel(submission.version)} on the grid it occupies — shaded square is the footprint.
                </div>
            </div>
        `;
    }

    _renderStaffPhase(phase) {
        const isSubmitted = phase.status === 'submitted';
        const isAccepted = phase.status === 'accepted';
        const cssClass = isSubmitted
            ? 'submitted'
            : isAccepted
              ? 'accepted'
              : phase.status === 'changes-requested'
                ? 'changes-requested'
                : '';

        return html`
            <div class="phase-step ${cssClass}">
                <div class="phase-step-header">
                    <span class="phase-step-title">${phaseDisplayName(phase.phase_type)}</span>
                    ${this._phaseStatusBadge(phase.status)}
                </div>

                ${
                    phase.review_note
                        ? html`
                    <div class="text-muted small mb-2"><em>${phase.review_note}</em></div>
                `
                        : nothing
                }

                ${
                    phase.submissions && phase.submissions.length > 0
                        ? html`
                    <div class="submission-grid">
                        ${phase.submissions.map(
                            (s) => html`
                            <div class="submission-thumb ${phase.approved_submission_id === s.id ? 'approved' : ''} ${isSubmitted && this._selectedSubmissionId === s.id ? 'selected' : ''}"
                                 @click="${() => {
                                     if (isSubmitted) this._selectedSubmissionId = s.id;
                                 }}">
                                <media-thumb src="${this._renderedLayerUrl(s) || s.file_url}" type="${this._renderedLayerUrl(s) ? 'image/png' : s.mime_type || ''}" alt="${versionLabel(s.version)}"
                                     @media-click="${(e) => {
                                         e.stopPropagation();
                                         this._lightboxSrc =
                                             this._renderedLayerUrl(s) || s.file_url;
                                     }}"></media-thumb>
                                ${this._renderSubmissionProcessing(s)}
                                <div class="submission-thumb-info">
                                    ${versionLabel(s.version)}
                                    ${phase.approved_submission_id === s.id ? html` <i class="bi bi-check-circle-fill text-success"></i>` : nothing}
                                </div>
                            </div>
                        `,
                        )}
                    </div>
                    ${this._renderMiniatureGridPreview(phase)}
                    ${
                        this._selected?.asset_type === 'mapmaking-tile-brush' &&
                        phase.submissions[0]?.layers
                            ? html`
                        <tile-brush-previews .layers="${phase.submissions[0].layers}"></tile-brush-previews>
                    `
                            : nothing
                    }
                `
                        : nothing
                }

                ${
                    isSubmitted
                        ? html`
                    <div class="mt-3">
                        <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Review note (optional for accept, required for changes)"
                            .value="${this._reviewNote}"
                            @input="${(e) => (this._reviewNote = e.target.value)}"></textarea>
                        <div class="d-flex gap-2">
                            <button class="btn btn-success btn-sm" ?disabled="${this._saving || (!this._selectedSubmissionId && phase.submissions.length !== 1)}"
                                @click="${() => this._acceptPhase(phase.id, phase.submissions)}">
                                Accept
                            </button>
                            <button class="btn btn-warning btn-sm" ?disabled="${this._saving || !this._reviewNote}"
                                @click="${() => this._requestChanges(phase.id)}">
                                Request Changes
                            </button>
                        </div>
                    </div>
                `
                        : nothing
                }
            </div>
        `;
    }

    _renderModal() {
        const c = this._selected;
        if (!c || !this._modal) return nothing;

        const isStyleGuide = this._modal === 'style-guide';
        const title = isStyleGuide ? 'Style Guide' : 'Technical Specification';
        const content = isStyleGuide ? c.style_guide : c.technical_spec;
        const filename = isStyleGuide
            ? `${c.asset_type}-style-guide.md`
            : `${c.asset_type}-technical-spec.md`;

        if (!content) return nothing;

        return html`
            <div class="modal-overlay" @click="${(e) => {
                if (e.target === e.currentTarget) this._modal = null;
            }}">
                <div class="modal-panel">
                    <div class="modal-header">
                        <h5>${title}</h5>
                        <button class="btn-close" @click="${() => (this._modal = null)}"></button>
                    </div>
                    <div class="modal-body">
                        <div class="spec-text">${content}</div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-outline-secondary btn-sm" @click="${() => downloadMarkdown(filename, content)}">
                            <i class="bi bi-download"></i> Download .md
                        </button>
                        <button class="btn btn-secondary btn-sm" @click="${() => (this._modal = null)}">Close</button>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'detail' ? this._renderDetail() : ''}
            ${this._view === 'activity' ? this._renderActivity() : ''}
            ${this._renderModal()}
        `;
    }
}

customElements.define('staff-assignments-view', StaffAssignmentsView);
